import { Injectable } from "@cervval.js/common/src/dependencies/injectable";
import { Permission, PermissionString } from "../dtos.permissions";
import { deserializePermission } from "../utils/utils.permissions";
import { PERMISSION_SERVICE, PermissionService } from "./permission.service";

/**
 * Implementation of the {@link PermissionService }
 * This implementation requires you to implement the getMergedPermissions to retrieve the permissions 
 * corresponding to a user (usually stored in its loginData, and might be dependant of the permissionContext)
 */
@Injectable(PERMISSION_SERVICE)
export abstract class AbstractPermissionService<LoginData, PermissionContext, PS extends PermissionString = PermissionString> implements PermissionService<LoginData, PermissionContext, PS> {

    /**
     * Returns the list of all the highest permissions without duplicates for the user - You can override it.
     * @param loginData Uid of the user
     * @param permissionContext (optional) dictionary of key:entityType / value: entityUid
     * @returns Permission[]
     */
    public abstract getMergedPermissions(loginData: LoginData, permissionContext?: PermissionContext): Permission[];

    /**
     * Check if a user as the given permission 
     * @param loginData data of the current logged-in user
     * @param permissions permission to check
     * @param permissionContext (optional) dictionary of key:entityType / value: entityUid
     * @returns True if has permission
     */
    public hasPermission(loginData: LoginData, permissions: PS | PS[], permissionContext?: PermissionContext, matchAllPermission?: boolean): boolean {
        const mergedPermissions = this.getMergedPermissions(loginData, permissionContext);
        // By default we want to match all permissions
        const optionMatchAllPermission = matchAllPermission === false ? false : true;
        return this._hasNecessaryPermissions(mergedPermissions, permissions, optionMatchAllPermission);
    }


    /**
     * Returns a granular list of all matching permissions for a user
     */
    public filterFieldByPermission<DTO>(loginData: LoginData, globalPermission: Permission, fields: (keyof DTO)[], permissionContext?: PermissionContext): string[] {
        const mergedPermissions = this.getMergedPermissions(loginData, permissionContext);

        return fields.map(field => <PS>`${globalPermission.scope}:${globalPermission.name}.+${<string>field}:${globalPermission.rights}`)
            .map(strPermission => deserializePermission(strPermission))
            .filter(permissionToCheck => {
                const correspondingPermissions = this._getCorrespondingPermissions(permissionToCheck, mergedPermissions);
                return correspondingPermissions.length > 0;
            })
            .map(permissionToCheck => permissionToCheck.includedField![0]);
    }

    /**
     * Return matching permissions in a list
     */
    protected _getCorrespondingPermissions(permissionToCheck: Permission, availablePermissions: Permission[]): Permission[] {

        const safeArray = (array?: string[]): string[] => array ?? [];

        // TODO this is not performant => build records and cache result (not urgent)
        const correspondingPermissions = availablePermissions.filter(ap => {// AvailablePermission
            // Must be on the same scope
            if (ap.scope !== permissionToCheck.scope) { return false; }
            // Must have at least the minimum rights (ex: if we request for read rights(r) and roles contains read/write(rw) it should work)
            if (!ap.rights.startsWith(permissionToCheck.rights)) { return false; }

            // Must be the same name 
            const nameMatch = ap.name === permissionToCheck.name || ap.name === "*";
            if (!ap.includedField?.length && !ap.excludedField?.length) {
                return nameMatch;
            }
            else if (ap.includedField?.length) {
                const hasAllIncluded = permissionToCheck.includedField?.every(i => safeArray(ap.includedField).includes(i));
                return nameMatch && hasAllIncluded;
            } else {
                const haveNoneExcluded = Boolean(permissionToCheck.includedField?.length && !safeArray(permissionToCheck.includedField).some(i => safeArray(ap.excludedField).includes(i)));
                const hasAllExcluded = Boolean(permissionToCheck.excludedField?.length && safeArray(ap.excludedField).every(i => permissionToCheck.excludedField!.includes(i))); // TODO :!VERIFY
                return nameMatch && (haveNoneExcluded || hasAllExcluded);
            }
        });
        return correspondingPermissions;
    }

    /**
     * Verify if mergedPermissions array correspond to necessaryPermissions
     * @param mergedPermissions Permissions of user with highest permissions without duplicates for the user
     * @param necessaryPermissions permission to check
     * @param matchAllPermissions Option if we want that mergedPermissions correspond to all necessaryPermissions or just one
     * @returns True if mergedPermissions correspond to necessaryPermissions
     */
    protected _hasNecessaryPermissions(mergedPermissions: Permission[], necessaryPermissions: PS | PS[], matchAllPermissions = true): boolean {
        // Make sure we have an array
        const necessaryPermissionsArray: PS[] = Array.isArray(necessaryPermissions) ? necessaryPermissions : [necessaryPermissions];
        // Check all permissions
        for (const permission of necessaryPermissionsArray) {
            // Do we have any permission matching the required one
            const correspondingPermissions: Permission[] = this._getCorrespondingPermissions(deserializePermission(permission), mergedPermissions);
            const hasNecessaryPermissions: boolean = correspondingPermissions?.length > 0;
            // Fast return, depending on the mode of matchAllPermissions (or = false / and = true)
            if (matchAllPermissions) {
                if (!hasNecessaryPermissions) {
                    // At least one permission is not matched, we won't be able to comply all permissions
                    return false;
                }
            } else {
                if (hasNecessaryPermissions) {
                    // At least one permission is matched, this in sufficient
                    return true;
                }
            }
        }
        // So we passed all the permissions ...
        if (matchAllPermissions) {
            // We did pass all the required permissions
            return true;
        } else {
            // We did not passed at least one required permission
            return false;
        }
    }

}